import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Separator } from '@/shared/components/ui/separator'

interface DetailDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  /** Form id — must match the <form id> inside children */
  formId: string
  isSubmitting?: boolean
  submitLabel?: string
  /** Max dialog width class, defaults to sm:max-w-lg */
  maxWidth?: string
  /** Optional slot rendered on the left side of the footer, before Cancel/Submit */
  footerLeft?: React.ReactNode
  /** Optional icon rendered to the left of the title */
  icon?: React.ReactNode
  /** Optional subtitle rendered below the title via DialogDescription */
  description?: string
  children: React.ReactNode
}

/**
 * Generic Dialog wrapper for create/edit forms.
 *
 * The parent component owns the <form> element (via react-hook-form).
 * Pass a `formId` that matches the form's `id` attribute so the submit
 * button can trigger validation across the dialog boundary.
 *
 * Usage:
 *   <DetailDialog formId="bank-account-form" ...>
 *     <form id="bank-account-form" onSubmit={handleSubmit(onSubmit)}>
 *       ...fields...
 *     </form>
 *   </DetailDialog>
 */
export function DetailDialog({
  open,
  onOpenChange,
  title,
  formId,
  isSubmitting = false,
  submitLabel = 'Save',
  maxWidth = 'sm:max-w-lg',
  footerLeft,
  icon,
  description,
  children,
}: DetailDialogProps) {
  // English `defaultValue` is the fallback when no i18n instance is ready (tests).
  const { t } = useTranslation()
  return (
    <Dialog open={open} onOpenChange={isSubmitting ? undefined : onOpenChange}>
      <DialogContent className={maxWidth}>
        <DialogHeader>
          <div className="flex items-center gap-2">
            {icon}
            <DialogTitle>{title}</DialogTitle>
          </div>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <Separator />
        <div className="py-2 px-1 overflow-y-auto max-h-[65vh]">{children}</div>
        <Separator />
        <DialogFooter className="gap-2 sm:justify-between">
          <div className="flex items-center">{footerLeft}</div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button type="submit" form={formId} disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {submitLabel}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
